<script setup>
import {computed, onMounted, ref, watch} from "vue";
import Button from "../../components/forms/Button.vue";
import {useRouter} from "vue-router";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import IntermediaryPersonsService from "@/services/IntermediaryPersonsService";
import CreateUpdateIntermediaryPersonModal from "@/components/modals/CreateUpdateIntermediaryPersonModal.vue";
import {getLoggedUser} from "@/services/AuthService";
import {translateRole} from "@/helper";
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const router = useRouter();
const intermediaryPersonId = router.currentRoute.value.params.id;
const intermediaryPerson = ref({});
const isLoading = ref(true);
const isEditable = ref(false);

/** Iniciály ze jména a příjmení — watch místo computed, protože celý ref je nahrazen z API */
const personInitials = ref('');

/** Aktivní záložka */
const activeTab = ref('basic');

/** Záložka Mentor se zobrazí pouze pokud existuje alespoň jedno z mentorských polí */
const hasMentorData = computed(() => {
  const p = intermediaryPerson.value;
  return 'mentor_firstname' in p || 'mentor_surname' in p || 'registration_number' in p;
});

onMounted(() => {
  fetchIntermediaryPerson();
  getLoggedUser().then(response => {
    const loggedUser = response;
    // IMA-510: MANAGER (celé SFA) i LEADER (vlastní podstrom) smí upravovat své poradce - backend
    // to vynucuje přes assertPersonVisible, mimo vlastní strukturu se sem vůbec nedostanou.
    if (['ADMIN', 'SUPERADMIN', 'BACK_OFFICE', 'INTERMEDIARY_MANAGER', 'INTERMEDIARY_LEADER'].includes(loggedUser.role)) {
      isEditable.value = true;
    }
  });
  // Parametr v callbacku přejmenován na 'person', aby nestínil ref intermediaryPerson
  emitter.on('intermediaryPersonSaved', (person) => {
    intermediaryPerson.value = person;
  });
});

/**
 * Reaguje na nahrazení celého objektu intermediaryPerson z API.
 * Skládá iniciály z firstname + surname.
 */
watch(intermediaryPerson, (val) => {
  if (!val) {
    personInitials.value = '?';
    return;
  }
  const f = (val.firstname || '').charAt(0).toUpperCase();
  const s = (val.surname || '').charAt(0).toUpperCase();
  personInitials.value = (f + s) || '?';
}, {deep: true});

function fetchIntermediaryPerson() {
  isLoading.value = true;
  IntermediaryPersonsService.getIntermediaryPerson(intermediaryPersonId).then((response) => {
    intermediaryPerson.value = response;
    isLoading.value = false;
  }, () => {
    // IMA-510: poradce mimo vlastní strukturu (403 od backendu) - hláška už zobrazil
    // IntermediaryPersonsService, tady jen neuvízneme navždy na "načítání".
    isLoading.value = false;
    router.back();
  });
}

function updateIntermediaryPerson() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateUpdateIntermediaryPersonModal, intermediaryPerson.value ? {
    intermediaryPerson: intermediaryPerson.value,
    intermediary: intermediaryPerson.value.intermediary
  } : {});
  reveal();
  // Parametr přejmenován na 'person', aby nestínil ref intermediaryPerson
  onConfirm((person) => {
    intermediaryPerson.value = person;
  });
  onCancel(() => {});
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-4">
          <a @click="router.back()" class="page-section__heading__back">
            <i class="mdi mdi-arrow-left"></i> {{ t('intermediaryPeopleDetail.back') }}
          </a>
        </div>
        <div class="col-4 text-center">
          <div class="page-section__title">{{ t('intermediaryPeopleDetail.detail_intermediaries_people') }}</div>
        </div>
        <div class="col-4"></div>
      </div>
    </div>

    <div class="page-section__content">

      <!-- Vizitka zástupce -->
      <div class="card mb-4">
        <div class="card-body py-3">
          <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">

            <!-- Levá část: avatar + jméno + role badge -->
            <div class="d-flex align-items-start gap-3">
              <!-- Kruhový avatar s iniciálami -->
              <div class="person-avatar">{{ personInitials }}</div>

              <div>
                <!-- Jméno příjmení + role badge na jednom řádku -->
                <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                  <h5 class="mb-0 fw-semibold">
                    {{ intermediaryPerson.firstname ?? '' }} {{ intermediaryPerson.surname ?? '---' }}
                  </h5>
                  <span v-if="intermediaryPerson.role" class="badge bg-secondary fw-normal">
                    {{ translateRole(intermediaryPerson.role) }}
                  </span>
                </div>
                <!-- Zprostředkovatel pod jménem -->
                <div v-if="intermediaryPerson.intermediary?.intermediary_name" class="text-muted small">
                  {{ intermediaryPerson.intermediary.intermediary_name }}
                </div>
              </div>
            </div>

            <!-- Pravá část: kontaktní info -->
            <div class="text-end small text-muted">
              <div v-if="intermediaryPerson.email" class="mb-1">{{ intermediaryPerson.email }}</div>
              <div v-if="intermediaryPerson.phone">{{ intermediaryPerson.phone }}</div>
            </div>

          </div>
        </div>
      </div>

      <!-- Karta s údaji — titulek + Upravit v hlavičce, záložky v card-body -->
      <div class="row">
        <div class="col-12 col-md-6">
          <div class="card">
            <div class="card-header">
              <div class="row">
                <div class="col">
                  <div class="card-header__title">{{ t('intermediaryPeopleDetail.person_data') }}</div>
                </div>
                <div class="col text-end">
                  <div class="card-header__action">
                    <Button v-if="isEditable" :label="t('intermediaryPeopleDetail.edit')" @click="updateIntermediaryPerson()"/>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-body">

              <!-- Záložky — navigace -->
              <ul class="nav nav-tabs mb-3">
                <li class="nav-item">
                  <a class="nav-link ps-2 pe-3" :class="{ active: activeTab === 'basic' }" href="#" @click.prevent="activeTab = 'basic'">
                    {{ t('intermediaryPeopleDetail.tab_basic') }}
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link px-3" :class="{ active: activeTab === 'contact' }" href="#" @click.prevent="activeTab = 'contact'">
                    {{ t('intermediaryPeopleDetail.tab_contact') }}
                  </a>
                </li>
                <!-- Záložka Mentor — zobrazí se jen pokud má zástupce mentorská data -->
                <li v-if="hasMentorData" class="nav-item">
                  <a class="nav-link px-3" :class="{ active: activeTab === 'mentor' }" href="#" @click.prevent="activeTab = 'mentor'">
                    {{ t('intermediaryPeopleDetail.tab_mentor') }}
                  </a>
                </li>
              </ul>

              <!-- Záložka: základní informace -->
              <div v-show="activeTab === 'basic'" class="info-table">
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.surname') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.surname ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.name') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.firstname ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.intermediary') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson?.intermediary?.intermediary_name ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.role') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.role ? translateRole(intermediaryPerson.role) : '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.parent') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.parent?.label ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.referral_code') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.referral_code ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.external_id') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.external_id ?? '---' }}</span>
                </div>
              </div>

              <!-- Záložka: kontakt -->
              <div v-show="activeTab === 'contact'" class="info-table">
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.email') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.email ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.phone_number') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.phone ?? '---' }}</span>
                </div>
              </div>

              <!-- Záložka: mentor — zobrazí se jen pokud existují data -->
              <div v-if="hasMentorData" v-show="activeTab === 'mentor'" class="info-table">
                <div v-if="'mentor_firstname' in intermediaryPerson" class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.mentor_firstname') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.mentor_firstname ?? '---' }}</span>
                </div>
                <div v-if="'mentor_surname' in intermediaryPerson" class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.mentor_surname') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.mentor_surname ?? '---' }}</span>
                </div>
                <div v-if="'registration_number' in intermediaryPerson" class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediaryPeopleDetail.registration_number') }}</span>
                  <span class="info-table__value col-12">{{ intermediaryPerson.registration_number ?? '---' }}</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* Avatar zástupce — kruh s iniciálami */
.person-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--primaryColor);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 600;
  flex-shrink: 0;
}

/* Nav-tabs: pouze spodní podtržení aktivní záložky (bez výplně) */
:deep(.nav-tabs) {
  border-bottom: none;
}
:deep(.nav-tabs .nav-link) {
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--bs-body-color);
  padding-bottom: 0.75rem;
}
:deep(.nav-tabs .nav-link.active) {
  border-bottom-color: var(--primaryColor);
  color: var(--primaryColor);
  background: transparent;
  font-weight: 600;
}
:deep(.nav-tabs .nav-link:hover:not(.active)) {
  border-bottom-color: #dee2e6;
}
</style>